import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { AudioLines, History, BookA, Zap, BarChart3, Settings, LogOut, Crown, Gift, Shield, Keyboard } from "lucide-react";
import TrialBanner from "@/components/TrialBanner";
import AnnouncementBanner from "@/components/AnnouncementBanner";
import FloatingMic from "@/components/FloatingMic";
import { MobileTabBar } from "@/components/MobileTabBar";

const links = [
  { to: "/flow", label: "Flow", icon: AudioLines },
  { to: "/history", label: "History", icon: History },
  { to: "/dictionary", label: "Dictionary", icon: BookA },
  { to: "/snippets", label: "Snippets", icon: Zap },
  { to: "/stats", label: "Insights", icon: BarChart3 },
  { to: "/billing", label: "Plan", icon: Crown },
  { to: "/invite", label: "Invite", icon: Gift },
  { to: "/download", label: "Get the app", icon: Keyboard },
  { to: "/settings", label: "Settings", icon: Settings },
];

export const Brand = () => (
  <div className="flex items-center gap-2.5">
    <span className="w-8 h-8 rounded-[10px] bg-[var(--accent)] flex items-center justify-center text-black"><AudioLines size={18} /></span>
    <span className="font-serif text-2xl leading-none text-[var(--ink)]">Mulankan</span>
  </div>
);

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const out = () => { logout(); navigate("/"); };
  const nav = user?.role === "admin" ? [...links, { to: "/admin", label: "Admin", icon: Shield }] : links;
  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-white/[0.06] text-[var(--ink)]" : "text-white/45 hover:text-white/85"}`;

  return (
    <div className="flow-bg min-h-screen flex" data-testid="app-shell">
      <aside className="hidden md:flex flex-col w-60 shrink-0 border-r border-white/[0.06] p-5 sticky top-0 h-screen">
        <div className="mb-10"><Brand /></div>
        <nav className="flex-1 space-y-1">
          {nav.map((l) => (
            <NavLink key={l.to} to={l.to} data-testid={`nav-${l.to.slice(1)}`} className={linkCls}>
              <l.icon size={17} /> {l.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/[0.06] pt-4">
          <div className="px-3 mb-2 text-xs text-white/40 truncate" data-testid="sidebar-user-email">{user?.email}</div>
          <button onClick={out} data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-white/45 hover:text-rose-300 transition-colors">
            <LogOut size={17} /> Log out
          </button>
        </div>
      </aside>
      <div className="flex-1 flex flex-col min-w-0">
        <div className="md:hidden sticky top-0 z-40 flex items-center justify-between px-4 py-3 border-b border-white/[0.06] bg-[#0D0B09]/90 backdrop-blur-xl" style={{ paddingTop: "max(0.75rem, env(safe-area-inset-top))" }}>
          <Brand />
          <span className="text-[11px] text-white/35 truncate max-w-[45%]" data-testid="mobile-user-email">{user?.email}</span>
        </div>
        <AnnouncementBanner />
        <TrialBanner />
        <main className="flex-1 px-4 sm:px-10 lg:px-14 py-6 sm:py-12 pb-32 md:pb-28">{children}</main>
      </div>
      <FloatingMic />
      <MobileTabBar nav={nav} email={user?.email} onLogout={out} />
    </div>
  );
}
